const DRAG_THRESHOLD = 5;

class PromoSlider extends HTMLElement {
  private scroller: HTMLElement | null = null;
  private track: HTMLElement | null = null;
  private thumb: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private rafId: number | null = null;
  private pointerId: number | null = null;
  private dragStartX = 0;
  private dragStartScrollLeft = 0;
  private dragged = false;

  connectedCallback(): void {
    this.scroller = this.querySelector<HTMLElement>("[data-promo-scroller]");
    this.track = this.querySelector<HTMLElement>("[data-promo-track]");
    this.thumb = this.querySelector<HTMLElement>("[data-promo-thumb]");

    if (!this.scroller || !this.track || !this.thumb) return;

    this.scroller.addEventListener("scroll", this.onScroll, { passive: true });
    this.scroller.addEventListener("pointerdown", this.onPointerDown);
    this.scroller.addEventListener("pointermove", this.onPointerMove);
    this.scroller.addEventListener("pointerup", this.onPointerEnd);
    this.scroller.addEventListener("pointercancel", this.onPointerEnd);
    this.scroller.addEventListener("dragstart", this.onDragStart);
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.scroller);
    this.update();
  }

  disconnectedCallback(): void {
    this.scroller?.removeEventListener("scroll", this.onScroll);
    this.scroller?.removeEventListener("pointerdown", this.onPointerDown);
    this.scroller?.removeEventListener("pointermove", this.onPointerMove);
    this.scroller?.removeEventListener("pointerup", this.onPointerEnd);
    this.scroller?.removeEventListener("pointercancel", this.onPointerEnd);
    this.scroller?.removeEventListener("dragstart", this.onDragStart);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    if (!this.scroller) return;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.scroller.scrollLeft;
    this.dragged = false;
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId || !this.scroller) return;
    const dx = event.clientX - this.dragStartX;
    if (!this.dragged) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      this.dragged = true;
      this.scroller.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
    this.scroller.scrollLeft = this.dragStartScrollLeft - dx;
  };

  private onPointerEnd = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId) return;
    const wasDragged = this.dragged;
    if (this.scroller?.hasPointerCapture(event.pointerId)) {
      this.scroller.releasePointerCapture(event.pointerId);
    }
    this.pointerId = null;
    this.dragged = false;
    if (wasDragged) {
      window.addEventListener("click", this.suppressClick, {
        capture: true,
        once: true,
      });
    }
  };

  private suppressClick = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  private onDragStart = (event: DragEvent): void => {
    if (this.pointerId !== null) event.preventDefault();
  };

  private onScroll = (): void => {
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  };

  private update = (): void => {
    if (!this.scroller || !this.track || !this.thumb) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
    const overflow = scrollWidth - clientWidth;

    if (overflow <= 0) {
      this.track.style.display = "none";
      return;
    }
    this.track.style.display = "";

    const ratio = clientWidth / scrollWidth;
    const progress = scrollLeft / overflow;
    const trackWidth = this.track.clientWidth;
    const thumbWidth = Math.max(trackWidth * ratio, 24);
    const maxOffset = trackWidth - thumbWidth;

    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${progress * maxOffset}px)`;
  };
}

if (!customElements.get("promo-slider")) {
  customElements.define("promo-slider", PromoSlider);
}

export default PromoSlider;
